/**
 * moonPhaseContent.ts
 *
 * Merged phase content combining:
 * - PHASE_CONTENT from @louise/care/education/phaseContent (clinical facts)
 * - Rich howItWorks/whatToExpect/prepInstructions/emotionalSupport pulled
 *   from i18n under `care.treatment_hub.phase_extensions.{phase}` and
 *   `care.treatment_hub.frozen_egg_ivf_overrides.{phase}`.
 */

import {
  getPhaseContent,
  PHASE_KEYS,
  type PhaseContentData,
} from "@louise/care/education/phaseContent";
import type { TreatmentPhaseName } from "@louise/care/treatment-phases";
import type { StepPerspective } from "@src/packages/homepage/sections/TreatmentProgressModal";
import type { TranslationFunction } from "@src/packages/i18n";

export interface MoonPhaseContentData extends PhaseContentData {
  howItWorks?: string;
  whatToExpect?: string[];
  prepInstructions?: string[];
  emotionalSupport?: string;
  /** Male-partner-perspective overrides. When the viewer is the AMAB partner
   * of an AFAB primary patient on a shared treatment, fields here replace the
   * canonical (self-perspective) values. */
  partner?: Partial<Omit<MoonPhaseContentData, "partner">>;
}

function tIndexedList(
  t: TranslationFunction,
  baseKey: string,
  maxItems = 12,
): string[] {
  const items: string[] = [];
  for (let i = 0; i < maxItems; i += 1) {
    const key = `${baseKey}.${i}`;
    const value = t(key);
    if (value === key) break;
    items.push(value);
  }
  return items;
}

function tOptional(t: TranslationFunction, key: string): string | undefined {
  const value = t(key);
  return value === key ? undefined : value;
}

function tOptionalList(
  t: TranslationFunction,
  baseKey: string,
): string[] | undefined {
  const items = tIndexedList(t, baseKey);
  return items.length ? items : undefined;
}

function getPhaseExtension(
  phase: TreatmentPhaseName,
  t: TranslationFunction,
): Omit<MoonPhaseContentData, keyof PhaseContentData> {
  const base = `care.treatment_hub.phase_extensions.${phase}`;
  const partnerBase = `${base}.partner`;

  const partnerDescription = tOptional(t, `${partnerBase}.description`);
  const partnerHowItWorks = tOptional(t, `${partnerBase}.how_it_works`);
  const partnerWhatHappens = tOptional(t, `${partnerBase}.what_happens`);
  const partnerWhatToExpect = tOptionalList(t, `${partnerBase}.what_to_expect`);
  const partnerPrepInstructions = tOptionalList(
    t,
    `${partnerBase}.prep_instructions`,
  );

  const hasPartner =
    partnerDescription !== undefined ||
    partnerHowItWorks !== undefined ||
    partnerWhatHappens !== undefined ||
    partnerWhatToExpect !== undefined ||
    partnerPrepInstructions !== undefined;

  return {
    howItWorks: tOptional(t, `${base}.how_it_works`),
    whatToExpect: tOptionalList(t, `${base}.what_to_expect`),
    prepInstructions: tOptionalList(t, `${base}.prep_instructions`),
    partner: hasPartner
      ? {
          description: partnerDescription,
          howItWorks: partnerHowItWorks,
          whatHappens: partnerWhatHappens,
          whatToExpect: partnerWhatToExpect,
          prepInstructions: partnerPrepInstructions,
        }
      : undefined,
  };
}

function getFrozenEggIvfOverride(
  phase: TreatmentPhaseName,
  t: TranslationFunction,
): Partial<MoonPhaseContentData> | undefined {
  const base = `care.treatment_hub.frozen_egg_ivf_overrides.${phase}`;
  const howItWorks = tOptional(t, `${base}.how_it_works`);
  const whatToExpect = tOptionalList(t, `${base}.what_to_expect`);
  const prepInstructions = tOptionalList(t, `${base}.prep_instructions`);
  if (
    howItWorks === undefined &&
    whatToExpect === undefined &&
    prepInstructions === undefined
  ) {
    return undefined;
  }
  return { howItWorks, whatToExpect, prepInstructions };
}

export function getMoonPhaseContentMap(
  t: TranslationFunction,
): Record<TreatmentPhaseName, MoonPhaseContentData> {
  const emotionalSupport = t(
    "care.treatment_hub.educational.emotional_support_default",
  );
  return Object.fromEntries(
    PHASE_KEYS.map((key) => {
      const base = getPhaseContent(key, t);
      const ext = getPhaseExtension(key, t);
      return [
        key,
        {
          ...base,
          ...ext,
          emotionalSupport,
          partner: ext.partner,
        },
      ];
    }),
  ) as Record<TreatmentPhaseName, MoonPhaseContentData>;
}

/**
 * Returns the phase content tuned for the viewer's perspective. When
 * `perspective === "partner"` and a `.partner` override exists on the phase,
 * its fields shadow the canonical values; otherwise returns canonical.
 */
export function getMoonPhaseContentForPerspective(
  phase: TreatmentPhaseName,
  perspective: StepPerspective,
  t: TranslationFunction,
): MoonPhaseContentData {
  const canonical = getMoonPhaseContentMap(t)[phase];
  if (perspective === "self" || !canonical.partner) return canonical;
  const p = canonical.partner;
  return {
    ...canonical,
    description: p.description ?? canonical.description,
    whatHappens: p.whatHappens ?? canonical.whatHappens,
    keyFacts: p.keyFacts ?? canonical.keyFacts,
    whenToCall: p.whenToCall ?? canonical.whenToCall,
    typicalDuration: p.typicalDuration ?? canonical.typicalDuration,
    title: p.title ?? canonical.title,
    howItWorks: p.howItWorks ?? canonical.howItWorks,
    whatToExpect: p.whatToExpect ?? canonical.whatToExpect,
    prepInstructions: p.prepInstructions ?? canonical.prepInstructions,
  };
}

export type CycleContentContext = {
  cycleId?: string | null;
  cycleType?: string | null;
  usingFrozenSpecimens?: boolean;
};

/**
 * Returns the phase content for a phase, applying cycle-context-specific
 * overrides where they exist.
 */
export function getMoonPhaseContent(
  phase: TreatmentPhaseName,
  t: TranslationFunction,
  context?: CycleContentContext,
): MoonPhaseContentData {
  const base = getMoonPhaseContentMap(t)[phase];
  const isFrozenEggIvf =
    context?.usingFrozenSpecimens === true &&
    (context.cycleType === "ivf" || context.cycleType === "icsi");
  if (isFrozenEggIvf) {
    const override = getFrozenEggIvfOverride(phase, t);
    if (override) return { ...base, ...override };
  }
  return base;
}
